import { useCallback, useEffect, useState } from "react";
import { useLingui } from "@lingui/macro";
import API from "@app/services/api";
import type {
  EurospineConsent,
  EurospineRunInfo,
  EurospineStep,
  EurospineVerifyResult,
} from "@app/components/PipelineView";

/**
 * The "Transfert EuroSpine" step of the Pipeline tab — the `eurospine` prop of `PipelineView`.
 *
 * The step stays hidden (`eligible: false`) unless the user may run the pipeline and the backend
 * says the session operates in the EuroSpine speciality.
 */
export function useEurospineStep(
  tenantId: string | undefined,
  patientId: string | undefined,
  canRunPipeline: boolean,
): EurospineStep {
  const { t } = useLingui();
  const [eligible, setEligible] = useState(false);
  const [lastRun, setLastRun] = useState<EurospineRunInfo | null>(null);
  // The last SUCCESSFUL transfer, not the last attempt: it says whether the registry already holds
  // this patient.
  const [lastSuccess, setLastSuccess] = useState<EurospineRunInfo | null>(null);
  const [verifyResult, setVerifyResult] =
    useState<EurospineVerifyResult | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<EurospineStep["outcome"]>(null);
  const [refreshingRegistry, setRefreshingRegistry] = useState(false);
  const [consent, setConsent] = useState<EurospineConsent | null>(null);
  const [refused, setRefused] = useState(false);
  const [togglingRefusal, setTogglingRefusal] = useState(false);

  // Eligibility + last-transfer status. Non-blocking: a failure leaves the step hidden.
  const loadStatus = useCallback(async () => {
    if (!patientId || !tenantId || !canRunPipeline) return null;
    try {
      const res = await API.get({
        path: `/patient/${tenantId}/${patientId}/eurospine`,
      });
      if (res?.ok) {
        setEligible(!!res.eligible);
        setLastRun(res.last_run ?? null);
        setLastSuccess(res.last_success ?? null);
        setConsent(res.consent ?? null);
        setRefused(!!res.refused);
        return res.last_success ?? null;
      }
    } catch {
      // ignored — the EuroSpine step stays hidden
    }
    return null;
  }, [patientId, tenantId, canRunPipeline]);

  // The registry's verdict lands minutes after the upload: re-read it on open, but only while it
  // can still change (`registry_final` = the registry is done with the file).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const last = await loadStatus();
      if (cancelled || !last || last.registry_final) return;
      try {
        const res = await API.post({
          path: `/patient/${tenantId}/${patientId}/eurospine/registry-status`,
        });
        if (!cancelled && res?.ok) {
          setLastRun(res.last_run ?? null);
          setLastSuccess(res.last_run ?? null);
        }
      } catch {
        // Registry unreachable: keep the last known verdict.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadStatus, tenantId, patientId]);

  const onVerify = async () => {
    setVerifying(true);
    setOutcome(null);
    try {
      const res = await API.post({
        path: `/patient/${tenantId}/${patientId}/eurospine/verify`,
      });
      if (res?.ok) {
        setVerifyResult({
          valid: !!res.valid,
          missing_required: res.missing_required ?? [],
          missing_fields: res.missing_fields ?? [],
          incoherent: res.incoherent ?? [],
          populated: res.populated ?? [],
          surgeon: res.surgeon ?? null,
          xml_bytes: res.xml_bytes ?? 0,
        });
      } else {
        alert(res?.detail || res?.error || t`Erreur lors de la vérification`);
      }
    } catch {
      alert(t`Erreur lors de la vérification`);
    } finally {
      setVerifying(false);
    }
  };

  // The SFTP upload does not say whether EuroSpine accepts the dossier: its parser runs minutes
  // later. Re-read that verdict on demand.
  const onRefreshRegistry = async () => {
    setRefreshingRegistry(true);
    try {
      const res = await API.post({
        path: `/patient/${tenantId}/${patientId}/eurospine/registry-status`,
      });
      if (res?.ok) {
        setLastRun(res.last_run ?? null);
      } else {
        alert(res?.detail || t`Statut EuroSpine indisponible`);
      }
    } catch {
      alert(t`Statut EuroSpine indisponible`);
    } finally {
      setRefreshingRegistry(false);
    }
  };

  const onSend = async () => {
    setSending(true);
    setOutcome(null);
    try {
      const res = await API.post({
        path: `/patient/${tenantId}/${patientId}/eurospine/send`,
      });
      if (res?.ok) {
        setOutcome("success");
        await loadStatus();
      } else {
        setOutcome("error");
        alert(
          res?.detail ||
            res?.error_message ||
            res?.error ||
            t`Erreur lors du transfert EuroSpine`,
        );
      }
    } catch {
      setOutcome("error");
      alert(t`Erreur lors du transfert EuroSpine`);
    } finally {
      setSending(false);
    }
  };

  /** A patient who refuses the registry is never sent; withdrawing the refusal is one click too. */
  const onToggleRefusal = async () => {
    setTogglingRefusal(true);
    try {
      const args = {
        path: `/patient/${tenantId}/${patientId}/eurospine/refusal`,
      };
      const res = refused ? await API.delete(args) : await API.post(args);
      if (res?.ok) {
        await loadStatus();
      } else {
        alert(res?.detail || res?.error || t`L'enregistrement a échoué.`);
      }
    } finally {
      setTogglingRefusal(false);
    }
  };

  return {
    eligible,
    lastRun,
    lastSuccess,
    verifyResult,
    verifying,
    sending,
    outcome,
    onVerify,
    onSend,
    refreshingRegistry,
    onRefreshRegistry,
    consent,
    refused,
    togglingRefusal,
    onToggleRefusal,
  };
}
